import { ContentState, convertFromRaw } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import { isDraftJSRawData } from 'utils';

export const toContentState = (styledText: string) => {
  if (isDraftJSRawData(styledText)) {
    return convertFromRaw(JSON.parse(styledText));
  }

  const contentBlock = htmlToDraft(styledText);
  return ContentState.createFromBlockArray(contentBlock.contentBlocks);
};


export interface IRichTextEditorV2Props {
  codeInjectionClassName?: string;
  data: string;
  disableDebounce?: boolean;
  htmlTag?: keyof JSX.IntrinsicElements;
  inline: boolean;
  isEditMode: boolean;
  maxLength?: number;
  onChange: (value: string) => void;
  onReady?: () => void;
  readOnly?: boolean;
  toolbarButtons: ToolbarButtons;
}

export interface ToolbarButtons {
  aligncenter?: boolean; // Center aligns the current block or image.
  alignjustify?: boolean; // Full aligns the current block or image.
  alignleft?: boolean; // Left aligns the current block or image.
  alignright?: boolean; // Right aligns the current block or image.
  blockquote?: boolean; // Applies block quote format to the current block-level element.
  bold?: boolean; // Applies the bold format to the current selection.
  bullist?: boolean; // Bullet list
  emoticons?: boolean; // Emoticons
  fontfamily?: boolean; // Dropdown list with font families to apply to selection.
  fontsize?: boolean; // Dropdown list with font sizes to apply to selection.
  forecolor?: boolean; // Applies foreground/text color to selection.
  imageLibrary?: boolean; // Custom image uploader to use our image library modal
  indent?: boolean; // Indents the current list item or block element.
  italic?: boolean; // Applies the italic format to the current selection.
  lineheight?: boolean; // Dropdown list with line heights to apply to selection.
  link?: boolean; // Apply link to texts
  numlist?: boolean; // Numbered list
  outdent?: boolean; // Outdents the current list item or block element.
  paragraphGroup?: boolean; // Align right/Justify/Numbered list/Outdent/Indent/Blockquote Paragraph group
  redo?: boolean; // To redo the last undone operation.
  removeformat?: boolean; // Removes the formatting from the current selection.
  strikethrough?: boolean; // Applies strike-through format to the current selection.
  styles?: boolean; // Dropdown list with styles to apply to selection.
  subscript?: boolean; // Applies subscript format to the current selection.
  superscript?: boolean; // Applies superscript format to the current selection.
  table?: boolean; // Table
  textGroup?: boolean;
  underline?: boolean; // Applies the underline format to the current selection.
  undo?: boolean; // To undo the last operation.
  videoLibrary?: boolean; // Video Library
}

export const TEXT_GROUP_ICON = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5498 19H15.5498L10.5498 5H8.5498L3.5498 19H5.5498L6.9498 15H12.0498L13.5498 19ZM7.64981 13L9.5498 7.8L11.4498 13H7.64981ZM20.4498 17.5C20.4498 17.7967 20.3618 18.0867 20.197 18.3334C20.0322 18.58 19.7979 18.7723 19.5238 18.8858C19.2497 18.9993 18.9481 19.0291 18.6572 18.9712C18.3662 18.9133 18.0989 18.7704 17.8891 18.5607C17.6794 18.3509 17.5365 18.0836 17.4786 17.7926C17.4207 17.5017 17.4505 17.2001 17.564 16.926C17.6775 16.6519 17.8698 16.4176 18.1164 16.2528C18.3631 16.088 18.6531 16 18.9498 16C19.3476 16 19.7292 16.158 20.0105 16.4393C20.2918 16.7206 20.4498 17.1022 20.4498 17.5ZM20.4498 13.5C20.4498 13.7967 20.3618 14.0867 20.197 14.3334C20.0322 14.58 19.7979 14.7723 19.5238 14.8858C19.2497 14.9994 18.9481 15.0291 18.6572 14.9712C18.3662 14.9133 18.0989 14.7704 17.8891 14.5607C17.6794 14.3509 17.5365 14.0836 17.4786 13.7926C17.4207 13.5017 17.4505 13.2001 17.564 12.926C17.6775 12.6519 17.8698 12.4176 18.1164 12.2528C18.3631 12.088 18.6531 12 18.9498 12C19.3476 12 19.7292 12.158 20.0105 12.4393C20.2918 12.7206 20.4498 13.1022 20.4498 13.5ZM20.4498 9.5C20.4498 9.79667 20.3618 10.0867 20.197 10.3334C20.0322 10.58 19.7979 10.7723 19.5238 10.8858C19.2497 10.9994 18.9481 11.0291 18.6572 10.9712C18.3662 10.9133 18.0989 10.7704 17.8891 10.5607C17.6794 10.3509 17.5365 10.0836 17.4786 9.79264C17.4207 9.50166 17.4505 9.20006 17.564 8.92597C17.6775 8.65189 17.8698 8.41762 18.1164 8.2528C18.3631 8.08797 18.6531 8 18.9498 8C19.3476 8 19.7292 8.15804 20.0105 8.43934C20.2918 8.72064 20.4498 9.10218 20.4498 9.5Z" fill="black"/></svg>';
export const PARAGRAPH_GROUP_ICON = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.68164 5.00005C6.7419 4.98485 5.82533 5.29219 5.08472 5.87085C4.3441 6.44951 3.82419 7.26453 3.61164 8.18005C3.39752 9.21903 3.60488 10.3005 4.18811 11.1866C4.77135 12.0727 5.68268 12.6909 6.72164 12.905L6.74864 12.91C7.01786 12.97 7.29284 13.0002 7.56864 13V19H9.56865V7.00005H11.5686V19H13.5686V7.00005H15.5686V5.00005H7.68164ZM5.53164 9.00005C5.53164 8.46962 5.74236 7.96091 6.11743 7.58584C6.4925 7.21076 7.00121 7.00005 7.53164 7.00005V11C7.00121 11 6.4925 10.7893 6.11743 10.4143C5.74236 10.0392 5.53164 9.53048 5.53164 9.00005ZM20.4716 17.491C20.4716 17.7877 20.3837 18.0777 20.2188 18.3244C20.054 18.5711 19.8198 18.7633 19.5457 18.8769C19.2716 18.9904 18.97 19.0201 18.679 18.9622C18.388 18.9043 18.1208 18.7615 17.911 18.5517C17.7012 18.3419 17.5583 18.0747 17.5005 17.7837C17.4426 17.4927 17.4723 17.1911 17.5858 16.917C17.6994 16.6429 17.8916 16.4087 18.1383 16.2438C18.385 16.079 18.675 15.991 18.9716 15.991C19.3695 15.991 19.751 16.1491 20.0323 16.4304C20.3136 16.7117 20.4716 17.0932 20.4716 17.491ZM20.4716 13.491C20.4716 13.7877 20.3837 14.0777 20.2188 14.3244C20.054 14.5711 19.8198 14.7633 19.5457 14.8769C19.2716 14.9904 18.97 15.0201 18.679 14.9622C18.388 14.9044 18.1208 14.7615 17.911 14.5517C17.7012 14.3419 17.5583 14.0747 17.5005 13.7837C17.4426 13.4927 17.4723 13.1911 17.5858 12.917C17.6994 12.6429 17.8916 12.4087 18.1383 12.2438C18.385 12.079 18.675 11.991 18.9716 11.991C19.3695 11.991 19.751 12.1491 20.0323 12.4304C20.3136 12.7117 20.4716 13.0932 20.4716 13.491ZM20.4716 9.49105C20.4716 9.78772 20.3837 10.0777 20.2188 10.3244C20.054 10.5711 19.8198 10.7633 19.5457 10.8769C19.2716 10.9904 18.97 11.0201 18.679 10.9622C18.388 10.9043 18.1208 10.7615 17.911 10.5517C17.7012 10.3419 17.5583 10.0747 17.5005 9.78368C17.4426 9.49271 17.4723 9.19111 17.5858 8.91702C17.6994 8.64294 17.8916 8.40867 18.1383 8.24385C18.385 8.07902 18.675 7.99105 18.9716 7.99105C19.3695 7.99105 19.751 8.14909 20.0323 8.43039C20.3136 8.71169 20.4716 9.09323 20.4716 9.49105Z" fill="black"/></svg>';
export const VIDEO_LIBRARY_ICON = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M23 6V18C23 19.1 22.1 20 21 20H3C1.9 20 1 19.1 1 18V6C1 4.9 1.9 4 3 4H21C22.1 4 23 4.9 23 6ZM21 8.8V7C21 6.44771 20.5523 6 20 6H4C3.44772 6 3 6.44772 3 7V17C3 17.5523 3.44772 18 4 18H20C20.5523 18 21 17.5523 21 17V8.8Z" fill="#F3F7FF"/> <path d="M10 14.1169V9.8831C10 9.49445 10.424 9.25439 10.7572 9.45435L14.2854 11.5713C14.6091 11.7655 14.6091 12.2345 14.2854 12.4287L10.7572 14.5457C10.424 14.7456 10 14.5056 10 14.1169Z" fill="#F3F7FF"/> </svg>';

type ToolbarButtonWithPlugin = {
  items: (keyof ToolbarButtons)[];
  plugin: string;
};

export const TOOLBAR_BUTTONS_WITH_PLUGINS: ToolbarButtonWithPlugin[] = [
  {
    plugin: 'emoticons',
    items: ['emoticons'],
  },
  {
    plugin: 'link',
    items: ['link'],
  },
  {
    plugin: 'table',
    items: ['table'],
  },
  {
    plugin: 'lists advlist',
    items: ['numlist', 'bullist'],
  },
];

/**
 * These are the three main (and only) versions of the rich text editor that we're gonna use.
 * It will render the toolbar buttons in sequence, so the order matter here
 */
export const PREDEFINED_TOOLBAR_BUTTONS: Record<'fullEditor' | 'titleEditor' | 'bodyEditor', ToolbarButtons> = {
  titleEditor: {
    fontsize: true,
    bold: true,
    italic: true,
    underline: true,
    forecolor: true,
  },
  bodyEditor: {
    fontsize: true,
    bold: true,
    italic: true,
    underline: true,
    forecolor: true,
    numlist: true,
    bullist: true,
    link: true,
  },
  fullEditor: {
    fontfamily: true,
    fontsize: true,
    bold: true,
    italic: true,
    underline: true,
    forecolor: true,
    textGroup: true,
    alignleft: true,
    aligncenter: true,
    numlist: true,
    paragraphGroup: true,
    link: true,
    imageLibrary: true,
    videoLibrary: true,
    emoticons: true,
    table: true,
    undo: true,
    redo: true,
  },
};

export const DEFAULT_FONT_FAMILY_FORMATS = 'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats';
